import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterOutlet,
} from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import { Pagination } from '@core/models/pagination.model';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { AccountManagementService } from '@core/services/account-management.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { LoadingService } from '@core/services/loading.service';
import { FiltersComponent } from '../shared/ui/filters/filters.component';
import { IFILTER } from 'app/app.constants';
import { ReportService } from '@core/services/report.service';
import { Store } from '@ngxs/store';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
@Component({
  selector: 'app-accounts',
  standalone: true,
  templateUrl: './accounts.component.html',
  styleUrl: './accounts.component.scss',
  providers: [TitleCasePipe],
  animations: [dropdownMenuAnimation],
  imports: [
    CommonModule,
    RouterOutlet,
    DropdownComponent,
    ButtonDirective,
    ReactiveFormsModule,
    FormsModule,
    TableNavigatorComponent,
    TableSkeletonLoaderComponent,
    FiltersComponent,
    PermissionDirective,
    TableRefreshComponent
  ],
})
export class AccountsComponent implements OnInit, OnDestroy {
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  showAction = false;
  actionList: DropDownModel[] = [];
  subs: Subscription[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  account: any;
  accountName: string | null = null;
  isExportDialogOpen: boolean = false;
  exportedKycUrl: string | null = null;
  isExportInProgress: boolean = false;
  accountType: string | null = null;
  $index: any;

  isFetchingAccountList$!: Observable<boolean>;

  private accountListSubject = new BehaviorSubject<any>(null);
  private totalCountSubject = new BehaviorSubject<number>(0);
  private accountMetadataSubject = new BehaviorSubject<any>(null);

  accountsList$ = this.accountListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  accountMetadata$: Observable<any> =
    this.accountMetadataSubject.asObservable();
  private hadFilterBefore = false;
  
  

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private notify = inject(NotificationFacade);
  private breadCrumbService = inject(BreadCrumbService);
  private accountService = inject(AccountManagementService);
  private change = inject(ChangeDetectorRef);
  private loadingService = inject(LoadingService);
  private reportService = inject(ReportService);
  private store = inject(Store);
  private confirmDialogService = inject(ConfirmDialogService);
  private permissionService = inject(RolesAndPermissionsService);


  loading$ = this.store.select(state => state.table.accounts?.loading);
  isFilterActive: boolean = false;

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },
    {
      filterKey: 'account_type',
      label: 'Account Type',
      type: 'dropdown',
      dropdown: [
        { key: 'corporate', value: 'Corporate' },
        { key: 'individual', value: 'Individual' },
        { key: 'joint', value: 'Joint' },
      ],
    },
    {
      filterKey: 'kyc_status',
      label: 'KYC Status',
      type: 'dropdown',
      dropdown: [
        { key: 'completed', value: 'Completed' },
        { key: 'incomplete', value: 'Incomplete' },
      ],
    },
    {
      filterKey: 'blocked',
      label: 'Access',
      type: 'dropdown',
      dropdown: [
        { key: 'true', value: 'Blocked' },
        { key: 'false', value: 'Unblocked' },
      ],
    },
    {
      filterKey: 'relationship_manager',
      label: 'Relationship Manager',
      type: 'dropdown',
      dropdown: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' },
      ],
    },
    {
      filterKey: 'annual_income',
      label: 'Annual Income',
      type: 'dropdown',
      dropdown: [
        { key: 'retail', value: 'Retail' },
        { key: 'hni', value: 'HNI' },
      ],
    },
    {
      filterKey: 'tier',
      label: 'Tier Level',
      type: 'dropdown',
      dropdown: [
        { key: 'one', value: 'Tier 1' },
        { key: 'two', value: 'Tier 2' },
        { key: 'three', value: 'Tier 3' },
      ],
    },
    {
      filterKey: 'delux_status',
      label: 'Deluxe Status',
      type: 'dropdown',
      dropdown: [
        { key: 'seeded', value: 'Seeded' },
        { key: 'non_seeded', value: 'Not Seeded' },
      ],
    },
  ];

  constructor(private titleCasePipe: TitleCasePipe) {
    this.accountsList$ = this.store.select(TableSelectors.getTableData('accounts'));
    this.totalCount$ = this.store.select(TableSelectors.getTableTotalCount('accounts'));
    this.accountMetadata$ = this.store.select(TableSelectors.getAccountsMetadata());
    this.isFetchingAccountList$ = this.store.select(TableSelectors.getTableLoading('accounts'));
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.loadAccountsList();
    this.getAccounMetadata();
    this.filterAccountListEventListener();
  }

  loadAccountsList(page = 1) {
    const sub = this.accountsList$.pipe(take(1)).subscribe(accounts => {
      const hasData = Array.isArray(accounts) && accounts.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(new TableActions.LoadTableData({
          tableKey: 'accounts',
          params: new Pagination({ 
            skip: (page - 1) * 10, 
            size: 10 
          }),
        }));
      }
    });
    this.subs.push(sub);
    
  }

  getAccounMetadata() {
    // if the account metadata is not in store, dispatch an action to get it
    const sub = this.accountMetadata$.subscribe(metadata => {
      if (!metadata) {
        this.store.dispatch(new TableActions.GetAccountsMetadata());
      }
    });
    this.subs.push(sub);
  }

  toggle() {
    this.change.detectChanges();
  }

  toggleFilterMenu() {
    this.filterMenuState =
      this.filterMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  filterAccountListEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[0].filterKey]) {
        query = `start_date=${params['start_date']}&`;
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params[this.filterStructure[2].filterKey]) {
        query += `account_type=${params['account_type']}&`;
      }
      if (params[this.filterStructure[3].filterKey]) {
        query += `kyc_status=${params['kyc_status']}&`;
      }
      if (params[this.filterStructure[4].filterKey]) {
        query += `blocked=${params['blocked']}&`;
      }
       if (params[this.filterStructure[5].filterKey]) {
        query += `relationship_manager=${params['relationship_manager']}&`;
      }
      if (params[this.filterStructure[6].filterKey]) {
        query += `annual_income=${params['annual_income']}&`;
      }
      if (params[this.filterStructure[7].filterKey]) {
        query += `tier=${params['tier']}&`;
      }
      if (params[this.filterStructure[8].filterKey]) {
        query += `delux_status=${params['delux_status']}&`;
      }

      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true; // filters are currently active
        this.store.dispatch(new TableActions.FilterAccountList({ filter: query, table: 'accounts' }));
      } else {
        this.isFilterActive = false;
  
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'accounts',
            params: new Pagination({ skip: skip, size: take }),
          }));
        }
      }
    });
  }
  getJointAccountName(account: any[]) {
    return account.reduce((p, c, i) => {
      const firstName = this.titleCasePipe.transform(c.personal_details.first_name);
      const middleName = this.titleCasePipe.transform(c.personal_details.middle_name);
      const lastName = c.personal_details.last_name?.toLocaleUpperCase();
      const fullName = `${firstName} ${middleName} ${lastName}`;
      
      if (i > 0) {
        return p + ` & ${fullName}`;
      }
      return fullName;
    }, '');
  }

  initDropdownList(param: any) {
    return (this.actionList = [
      {
        action: () => {
          this.viewAccountDetails(param);
        },
        title: 'View Details',
        show: true && this.permissionService.hasPermission(['get_individual']),
      },
      {
        action: () => {
          this.editAccountDetails(param);
        },
        title: 'Edit',
        show: true && this.permissionService.hasPermission(['get_individual']),
      },
      {
        action: () => {
          this.isExportDialogOpen = true;

          type AccountType = 'individual' | 'joint' | 'corporate';
          const exportKYC: Record<AccountType, (id: string) => void> = {
            individual: (id: string) => this.exportAccountKYC(id),
            joint: (id: string) => this.exportAccountKYC(id),
            corporate: (id: string) => this.exportAccountKYC(id),
          };
          exportKYC[param.account_type as AccountType]?.(param.id);

          this.accountName =
            param.account_type === 'individual'
              ? `${param.user_profile.first_name} ${param.user_profile.last_name}`
              : param.account_type === 'corporate'
                ? param.corporate_kyc?.name
                : this.getJointAccountName(param.joint_kyc) ||
                  'Empty Account Name';

          this.accountType = param.account_type;

          // Build dialog description dynamically
          const description = this.isExportInProgress
            ? `Exporting KYC for ${this.capitalize(this.accountType)} account: ${this.capitalize(
                this.accountName
              )}. Please do not close this page.`
            : `KYC File for ${this.capitalize(this.accountType)} account '${this.capitalize(
                this.accountName
              )}' exported successfully.`;

          // Open confirm dialog
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Open Exported KYC',
            description: description,
            loadingText: 'Exporting KYC...',
            action: () => {
              this.openKycDocument();
            },
          });
        },
        title: 'Export KYC',
        show: true && this.permissionService.hasPermission(['can_get_kyc_report']),
      },
      {
        title: 'Cash Statement',
        action: () => {
          this.router.navigate(['/admin/accounts/generate-cash-statement'], {
            queryParams: {
              accountId: param.id,
              accountType: param.account_type,
            },
          });
        },
        show: true && this.permissionService.hasPermission(['can_generate_cash_statement']),
      },
      {
        title: 'View Assigned RM',
        action: () => {
          this.router.navigateByUrl(
            `/admin/accounts/${param.account_type}/${param.id}/rm-list`
          );
        },
        show: true && this.permissionService.hasPermission(['get_individual']),
      },
      {
        title: 'Assign RM',
        action: () => {
          this.router.navigateByUrl(`/admin/accounts/${param.id}/assign-to-rm`);
        },
        show: true && this.permissionService.hasPermission(['assign_relationship_manager_to_account']),
      },
      {
        title: 'Unblock Account',

        action: () => {
          this.changeAccountStatus(param);
        },
        show: param.is_blocked == true && this.permissionService.hasPermission(['can_unblock_account']),
      },
      {
        title: 'Block Account',
        action: () => {
          this.changeAccountStatus(param);
        },
        show: param.is_blocked == false && this.permissionService.hasPermission(['can_block_account']),
      },
      {
        title: '+ MF Transaction',
        action: () => {
          this.navigateToAddMFTransaction(param.mango_account_number);
        },
        show: this.permissionService.hasPermission(['can_add_mutual_fund_subscription']),
      },
      {
        title: 'View MF Transaction',
        action: () => {
          this.viewAccountFTransaction(param.mango_account_number);
        },
        show: this.permissionService.hasPermission(['can_get_all_funds_transaction']),
      },
      {
        title: 'Create Joint Account',
        action: () => {
          console.log(param.user_profile.email);
          this.router.navigate([`/admin/accounts/joint-profile-details`], {
            queryParams: { email: param.user_profile.email },
          });
        },
        show: param.account_type === 'individual' && this.permissionService.hasPermission(['can_create_joint_account']),
      },
      {
        title: 'Create Minor Account',
        action: () => {
          this.router.navigateByUrl(`/admin/accounts/individual/accounts/${param.id}/create-minor-account`);
        },
        show: param.account_type === 'individual' && this.permissionService.hasPermission(['create_minor_profile']) && this.isAbove18(param.user_profile?.dob, param.created_at),
      },
      {
        title: 'Seed Deluxe ClientID',
        action: () => {
          this.seedAccountDeluxeClientId(param.mango_account_number);
        },
        show: !param.deluxe_client_id && this.permissionService.hasPermission(['seed_account_deluxe_client_id'])
      },
      {
        title: 'Attach account',
        action: () => {
          // corporate accounts have no user_id; the attach page picks a signatory instead
          this.router.navigate([param.account_type === 'corporate'
            ? `/admin/accounts/corporate/${param.id}/attach-account`
            : `/admin/accounts/individual/accounts/${param.id}/attach-account/${param.user_id}`]);
        },
        show: ['individual', 'corporate'].includes(param.account_type) &&this.permissionService.hasPermission(['can_create_additional_account'])
      },
      {
        title: 'Delete account',
        action: () => {
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Delete Account',
            description: `Are you sure you want to delete the: ${this.accountService.getAccountName(param)} - ${param.mango_account_number} account? This action cannot be undone.`,
            loadingText: 'Deleting Account...',
            bgConfirmButton: 'danger',
            action: () => this.deleteAccount(param.id),

          });
        },
        show: this.permissionService.hasPermission(['can_delete_account']),
      },
    ])
  }

  viewAccountFTransaction(accountNumber: string) {
    this.router.navigateByUrl(`/admin/transactions/mutual-funds/consolidated/${accountNumber}/mutual-funds-history`)
  }

  navigateToAddMFTransaction(accountNumber: string) {
    this.router.navigateByUrl(
      `/admin/transactions/mutual-funds/consolidated/add-transaction?account_number=${accountNumber}`
    );
  }

  registerationDropdownActions(): DropDownModel[] {
    return [
      {
        title: 'Individual',
        action: () => {
          this.router.navigateByUrl('/admin/accounts/ind-personal-details');
        },
        show: true && this.permissionService.hasPermission(['create_user']),
      },
      {
        title: 'Corporate',
        action: () => {
          this.router.navigateByUrl('/admin/accounts/corp-company-details');
        },
        show: true && this.permissionService.hasPermission(['create_coroporate']),
      },
      {
        title: 'Joint',
        action: () => {
          this.router.navigateByUrl('/admin/accounts/joint-profile-details');
        },
        show: true && this.permissionService.hasPermission(['can_create_joint_account']),
      },
    ];
  }

  searchAccountList(text: string) {
    const sub = this.accountService.searchAccount(text).subscribe({
      next: (res: any) => {
        this.accountListSubject.next(res.data.data);
        this.totalCountSubject.next(res.data.total_count);
      },
      error: () => { },
    });
    this.subs.push(sub);
  }

  changeAccountStatus(account: any) {
    if (account.is_blocked) {
      this.store.dispatch(new TableActions.UnblockAccount({accountId: account.id}));
    } else {
      this.store.dispatch(new TableActions.BlockAccount({accountId: account.id}));
    }

  }

  deleteAccount(accountId: any) {
    this.store.dispatch(new TableActions.DeleteAccount({accountId: accountId}));
    
    this.confirmDialogService.updateDialog({
      isDialogLoading: false,
      isOpen: false,
    });
  }

  viewAccountDetails(account: any) {
    this.router.navigateByUrl(
      `/admin/accounts/${account.account_type}/${account.id}`
    );
  }

  editAccountDetails(account: any) {
    this.router.navigateByUrl(
      `/admin/accounts/${account.account_type}/edit/${account.id}`
    );
  }

  deleteAccountDetails(account: any) {
    this.notify.error('Action not implemented');
  }

  getAccountListMetadata() {
    const sub = this.accountService.getAccountListMetaData().subscribe({
      next: (res) => {
        this.accountMetadataSubject.next(res.data);
      },
      error: () => { },
    });
    this.subs.push(sub);
  }

  getAccountList(page: Pagination = new Pagination()) {
    const sub = this.accountService.viewAccountList(page).subscribe({
      next: (res) => {
        this.accountListSubject.next(res.data.data);
        this.totalCountSubject.next(res.data.total_count);
      },
      error: () => { },
    });
    this.subs.push(sub);
  }


  exportAccountKYC(accountId: string) {
    this.isExportInProgress = true;
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    const sub = this.reportService.exportAccountKyc(accountId).subscribe({
      next: (response) => {
        this.isExportInProgress = false;
        this.confirmDialogService.updateDialog({
          isDialogLoading: false,
          isOpen: false,
        });
        this.exportedKycUrl = response.data.presigned_url;
        this.change.detectChanges();
      },
      error: () => {
        this.isExportInProgress = false;
        this.confirmDialogService.updateDialog({ isDialogLoading: false });
        this.exportedKycUrl = null;
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  assignRM() {
    this.notify.info('Action not yet implemented');
  }

  openKycDocument() {
    if (this.exportedKycUrl) {
      window.open(this.exportedKycUrl, '_blank');
    }
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }


  exportReport() {
    const urlWithoutQuery = this.router.url.split('?')[0];
    const currentQueryParams = this.activatedRoute.snapshot.queryParams;
    this.router.navigate([`${urlWithoutQuery}/export-report`], {
      queryParams: currentQueryParams,
    });
  }

    seedAccountDeluxeClientId(accountNumber: string) {
    const sub = this.accountService.seedAccountDeluxeClientId(accountNumber).subscribe({
      next: (res) => {
        this.notify.success('Deluxe ClientID seeded successfully');
      },
      error: () => { 
        this.notify.error('Failed to seed Deluxe ClientID');
      },
    });
    this.subs.push(sub);
  }

  capitalize(value: string | null): string {
    if (!value) return '';
    return value
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  isAbove18(dob: string | undefined, createdAt: string | undefined): boolean {
    if (!dob || !createdAt) return false;
    
    const dobDate = new Date(dob);
    const createdAtDate = new Date(createdAt);

    let age = createdAtDate.getFullYear() - dobDate.getFullYear();
    const monthDiff = createdAtDate.getMonth() - dobDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && createdAtDate.getDate() < dobDate.getDate())) {
      age--;
    }

    return age > 18;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
